'use client'

import * as React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { Menu } from 'lucide-react'

import type { NavItem } from '@/config/site'
import { siteConfig } from '@/config/site'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { track } from '@/lib/gtm'
import { ThemeToggle } from './theme-toggle'

const headerClasses =
  'sticky inset-x-0 top-0 z-50 border-b border-black/5 bg-white/80 pt-[env(safe-area-inset-top)] backdrop-blur-md transition dark:border-white/10 dark:bg-black/75'

const navItems: NavItem[] = siteConfig.nav
const authItems: NavItem[] = siteConfig.auth

export const SiteHeader = () => {
  const pathname = usePathname()
  const [open, setOpen] = React.useState(false)
  const [mounted, setMounted] = React.useState(false)

  React.useEffect(() => {
    setOpen(false)
  }, [pathname])

  // Simple scroll interaction to close mobile menu on hash click
  const handleLinkClick = () => {
    setOpen(false)
  }

  React.useEffect(() => {
    setMounted(true)
  }, [])

  return (
    <header className={headerClasses}>
      <div className="mx-auto flex h-14 w-full max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link
          href="/"
          onClick={handleLinkClick}
          aria-label="LuckAgents, volver al inicio"
          className="text-foreground flex items-center gap-2 text-lg font-bold tracking-tight transition-opacity hover:opacity-80"
        >
          <div className="relative flex h-7 w-7 items-center justify-center overflow-hidden rounded-[10px] bg-white shadow-sm ring-1 ring-black/5 dark:bg-white/10 dark:ring-white/10">
            <Image
              src="/brand/logo.svg"
              alt=""
              width={64}
              height={64}
              className="h-full w-full scale-[1.25] object-cover transition-[filter] duration-200 dark:invert"
              priority
            />
          </div>
          <span className="font-semibold text-gray-900 dark:text-gray-100">LuckAgents</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden items-center gap-6 md:flex">
          {navItems.map((item) => (
            <Link
              key={item.title}
              href={item.href}
              className="text-sm font-medium text-gray-600 transition-colors hover:text-black dark:text-gray-300 dark:hover:text-white"
            >
              {item.title}
            </Link>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden items-center gap-2 md:flex">
          <ThemeToggle className="h-8 w-8 rounded-full text-gray-600 hover:bg-black/5 hover:text-black dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white" />
          <Link
            href={siteConfig.secondaryCta.href}
            className="text-sm font-medium text-gray-600 transition-colors hover:text-black dark:text-gray-300 dark:hover:text-white"
          >
            {siteConfig.secondaryCta.label}
          </Link>
          <Button
            asChild
            size="sm"
            className="h-8 rounded-full bg-black px-4 text-xs font-medium text-white shadow-sm hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200"
            onClick={() =>
              track('cta_click', {
                place: 'header_primary',
                cta: siteConfig.primaryCta.label,
                href: siteConfig.primaryCta.href,
              })
            }
          >
            <Link href={siteConfig.primaryCta.href} prefetch={false}>
              {siteConfig.primaryCta.label}
            </Link>
          </Button>
        </div>

        {/* Mobile Menu */}
        {mounted && (
          <div className="flex items-center gap-1 md:hidden">
            <ThemeToggle className="h-9 w-9 rounded-full text-gray-600 hover:bg-black/5 hover:text-black dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white" />
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="-mr-2 h-9 w-9 text-gray-500 hover:bg-black/5 hover:text-black dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white"
                >
                  <Menu className="h-5 w-5" aria-hidden="true" />
                  <span className="sr-only">Abrir menú</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="top" className="w-full p-4 pt-14">
                <SheetHeader className="sr-only">
                  <SheetTitle>Menú de navegación</SheetTitle>
                  <SheetDescription>Enlaces principales</SheetDescription>
                </SheetHeader>
                <div className="flex flex-col gap-6">
                  <nav className="flex flex-col gap-4">
                    {navItems.map((item) => (
                      <Link
                        key={item.title}
                        href={item.href}
                        onClick={handleLinkClick}
                        className="text-lg font-medium text-gray-900 dark:text-gray-100"
                      >
                        {item.title}
                      </Link>
                    ))}
                  </nav>
                  <div className="flex flex-col gap-3">
                    <Button
                      asChild
                      className="w-full rounded-full bg-black text-white hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200"
                      onClick={() => {
                        handleLinkClick()
                        track('cta_click', {
                          place: 'header_mobile_primary',
                          cta: siteConfig.primaryCta.label,
                        })
                      }}
                    >
                      <Link href={siteConfig.primaryCta.href} prefetch={false}>
                        {siteConfig.primaryCta.label}
                      </Link>
                    </Button>
                    <div className="flex justify-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                      {authItems.map((item) => (
                        <Link
                          key={item.href}
                          href={item.href}
                          onClick={handleLinkClick}
                          prefetch={item.href !== '/register'}
                          className="hover:text-black dark:hover:text-white"
                        >
                          {item.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        )}
      </div>
    </header>
  )
}
